import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, 
  Lock, 
  ArrowRight, 
  AlertTriangle, 
  CheckCircle2, 
  Search,
  Filter,
  Building,
  Layers,
  ChevronDown
} from 'lucide-react';
import { api, MonthlyPlan, IncompletePlanTask } from '../../lib/api';

interface ClosePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: MonthlyPlan;
  availablePlans: MonthlyPlan[]; // Other plans to roll over to
  onSuccess: () => void;
}

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const ClosePlanModal: React.FC<ClosePlanModalProps> = ({
  isOpen,
  onClose,
  plan,
  availablePlans,
  onSuccess
}) => {
  const [incompleteTasks, setIncompleteTasks] = useState<IncompletePlanTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [targetPlanId, setTargetPlanId] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterBuilding, setFilterBuilding] = useState('All');
  const [showOnlyNotDone, setShowOnlyNotDone] = useState(true);

  useEffect(() => {
    if (isOpen && plan) {
      loadIncompleteTasks();
    }
  }, [isOpen, plan]);

  const loadIncompleteTasks = async () => {
    try {
      setLoading(true);
      const tasks = await api.getIncompleteTasks(plan.id);
      setIncompleteTasks(tasks);
      // Pre-select all tasks by default
      setSelectedTasks(tasks.map(t => t.plan_task_id));
    } catch (err) {
      console.error('Failed to load incomplete tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories and buildings for filters
  const categories = useMemo(() => {
    const cats = new Set(incompleteTasks.map(t => t.category_name).filter(Boolean));
    return ['All', ...Array.from(cats)];
  }, [incompleteTasks]);

  const buildings = useMemo(() => {
    const bldgs = new Set(incompleteTasks.map(t => t.building_name).filter(Boolean));
    return ['All', ...Array.from(bldgs)];
  }, [incompleteTasks]);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return incompleteTasks.filter(task => {
      // Not done filter (progress < 100 or null)
      if (showOnlyNotDone && task.progress !== null && task.progress >= 100) {
        return false;
      }

      // Search
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        if (!(task.task_name?.toLowerCase().includes(search) ||
              task.wbs_code?.toLowerCase().includes(search) ||
              task.building_name?.toLowerCase().includes(search))) {
          return false;
        }
      }

      // Category filter
      if (filterCategory !== 'All' && task.category_name !== filterCategory) {
        return false;
      }

      // Building filter
      if (filterBuilding !== 'All' && task.building_name !== filterBuilding) {
        return false;
      }

      return true;
    });
  }, [incompleteTasks, showOnlyNotDone, searchTerm, filterCategory, filterBuilding]);

  const toggleTaskSelection = (planTaskId: number) => {
    setSelectedTasks(prev => 
      prev.includes(planTaskId)
        ? prev.filter(id => id !== planTaskId)
        : [...prev, planTaskId]
    );
  };

  const toggleAll = () => {
    const filteredIds = filteredTasks.map(t => t.plan_task_id);
    const allSelected = filteredIds.every(id => selectedTasks.includes(id));
    
    if (allSelected) {
      // Deselect all filtered
      setSelectedTasks(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered
      setSelectedTasks(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const handleClose = async () => {
    try {
      setIsClosing(true);
      
      await api.closeMonthlyPlan(plan.id, {
        rolloverTaskIds: selectedTasks,
        targetPlanId: targetPlanId || undefined
      });

      let message = 'Plan closed successfully.';
      if (selectedTasks.length > 0) {
        message += ` ${selectedTasks.length} task(s) marked for rollover.`;
        if (targetPlanId) {
          const targetPlan = availablePlans.find(p => p.id === targetPlanId);
          message += ` Added to ${MONTHS[targetPlan?.month || 0]} ${targetPlan?.year}.`;
        }
      }
      
      alert(message);
      onSuccess();
      onClose();
    } catch (err: any) {
      alert(err.message || 'Failed to close plan');
    } finally {
      setIsClosing(false);
    }
  };

  if (!isOpen) return null;

  const filteredSelected = filteredTasks.filter(t => selectedTasks.includes(t.plan_task_id)).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Lock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Close Monthly Plan</h2>
              <p className="text-sm text-gray-500">
                {MONTHS[plan.month]} {plan.year} • {incompleteTasks.length} incomplete task{incompleteTasks.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-amber-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : incompleteTasks.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">All Tasks Complete!</h3>
              <p className="text-gray-500 text-center mb-6">
                All tasks in this plan have been completed.<br />
                You can safely close this plan.
              </p>
              <button
                onClick={handleClose}
                disabled={isClosing}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {isClosing ? 'Closing...' : 'Close Plan'}
              </button>
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className="p-4 border-b border-gray-200 space-y-3">
                <div className="flex items-center gap-3">
                  {/* Not Done Filter Toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showOnlyNotDone}
                      onChange={(e) => setShowOnlyNotDone(e.target.checked)}
                      className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Not Done Only</span>
                  </label>

                  {/* Search */}
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search tasks..."
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>

                  {/* Category Filter */}
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat === 'All' ? 'All Categories' : cat}</option>
                    ))}
                  </select>

                  {/* Building Filter */}
                  <select
                    value={filterBuilding}
                    onChange={(e) => setFilterBuilding(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  >
                    {buildings.map(bldg => (
                      <option key={bldg} value={bldg}>{bldg === 'All' ? 'All Buildings' : bldg}</option>
                    ))}
                  </select>
                </div>

                {/* Selection Summary */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleAll}
                      className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                    >
                      {filteredTasks.every(t => selectedTasks.includes(t.plan_task_id)) 
                        ? 'Deselect All' 
                        : 'Select All'}
                    </button>
                    <span className="text-sm text-gray-500">
                      ({filteredSelected} of {filteredTasks.length} selected for rollover)
                    </span>
                  </div>
                  
                  {selectedTasks.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Roll over to:</span>
                      <select
                        value={targetPlanId || ''}
                        onChange={(e) => setTargetPlanId(e.target.value ? parseInt(e.target.value) : null)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">Select later</option>
                        {availablePlans
                          .filter(p => p.id !== plan.id && p.status !== 'closed')
                          .map(p => (
                            <option key={p.id} value={p.id}>
                              {MONTHS[p.month]} {p.year} ({p.status})
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Task List */}
              <div className="flex-1 overflow-y-auto">
                {filteredTasks.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No tasks match your filters
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="w-10 px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={filteredTasks.length > 0 && filteredTasks.every(t => selectedTasks.includes(t.plan_task_id))}
                            onChange={toggleAll}
                            className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Building</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredTasks.map(task => (
                        <tr 
                          key={task.plan_task_id}
                          className={`hover:bg-gray-50 cursor-pointer ${
                            selectedTasks.includes(task.plan_task_id) ? 'bg-amber-50' : ''
                          }`}
                          onClick={() => toggleTaskSelection(task.plan_task_id)}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedTasks.includes(task.plan_task_id)}
                              onChange={() => toggleTaskSelection(task.plan_task_id)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">{task.task_name}</div>
                            <div className="text-xs text-gray-500">
                              {task.wbs_code} • {task.floor_name} • {task.unit_name}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700">{task.building_name}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                              {task.category_name}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    (task.progress || 0) >= 100 ? 'bg-green-500' :
                                    (task.progress || 0) > 0 ? 'bg-blue-500' :
                                    'bg-gray-300'
                                  }`}
                                  style={{ width: `${Math.min(task.progress || 0, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600">{task.progress || 0}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              task.source === 'rollover' 
                                ? 'bg-orange-100 text-orange-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {task.source === 'rollover' ? 'Rollover' : 'Master'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {incompleteTasks.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="text-gray-600">
                {selectedTasks.length === 0 
                  ? 'All incomplete tasks will be marked as completed'
                  : `${selectedTasks.length} task(s) will be marked for rollover`}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleClose}
                disabled={isClosing}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isClosing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Closing...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Close Plan
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClosePlanModal;

